import { z } from 'zod'
import { Action } from './actions.js'
import { Id, Scope, VariableValue } from './primitives.js'
import { TriggerTypeEnum } from './triggers.js'

/**
 * The runtime contract.
 *
 * Both rule engines — the one on the listener's device and the one on the
 * server — emit exactly these events, so logging, the operator console, trace
 * replay and analytics all read one vocabulary. The working rule for the whole
 * system is that if a behaviour is not in this stream, the console cannot show
 * it, replay cannot reproduce it, and it effectively did not happen.
 *
 * `seq` is monotonic per session and assigned by the server, which makes
 * duplicate delivery harmless and lets a reconnecting client ask for the gap.
 */
const eventBase = {
  seq: z.number().int().nonnegative(),
  /** Epoch milliseconds on the server clock. */
  ts: z.number().finite(),
  scope: Scope,
  /** The participant the event concerns; absent for session-wide events. */
  participantId: Id.optional(),
}

const RuleFiredEvent = z.object({
  type: z.literal('ruleFired'),
  ...eventBase,
  ruleId: Id,
  triggerType: TriggerTypeEnum,
})

/** Carries the resolved action so a replay reproduces the run exactly. */
const ActionExecutedEvent = z.object({
  type: z.literal('actionExecuted'),
  ...eventBase,
  ruleId: Id.optional(),
  action: Action,
})

export const EmitterRuntimeState = z.enum([
  'started',
  'ended',
  'stopped',
  'paused',
  'resumed',
  /** Dropped from the audible set by voice culling; playhead keeps running. */
  'virtualized',
  'restored',
])
export type EmitterRuntimeState = z.infer<typeof EmitterRuntimeState>

const EmitterStateEvent = z.object({
  type: z.literal('emitterState'),
  ...eventBase,
  emitterId: Id,
  state: EmitterRuntimeState,
})

const VariableChangedEvent = z.object({
  type: z.literal('variableChanged'),
  ...eventBase,
  name: z.string().max(64),
  value: VariableValue,
})

const ParticipantEvent = z.object({
  type: z.literal('participant'),
  ...eventBase,
  event: z.enum(['joined', 'left']),
  roles: z.array(z.enum(['listener', 'broadcaster', 'operator', 'editor'])).default(['listener']),
})

const CueFiredEvent = z.object({
  type: z.literal('cueFired'),
  ...eventBase,
  cueId: Id,
  byParticipantId: Id.optional(),
})

const AccuracyEvent = z.object({
  type: z.literal('accuracy'),
  ...eventBase,
  accuracyM: z.number().finite().nonnegative(),
  degraded: z.boolean(),
})

const SessionStateEvent = z.object({
  type: z.literal('sessionState'),
  ...eventBase,
  state: z.enum(['started', 'ended']),
})

export const SessionEvent = z.discriminatedUnion('type', [
  RuleFiredEvent,
  ActionExecutedEvent,
  EmitterStateEvent,
  VariableChangedEvent,
  ParticipantEvent,
  CueFiredEvent,
  AccuracyEvent,
  SessionStateEvent,
])
export type SessionEvent = z.infer<typeof SessionEvent>
export type SessionEventType = SessionEvent['type']

/**
 * An event before the server has assigned it a place in the session's order.
 *
 * The omit distributes over the union deliberately: a plain `Omit` would
 * collapse the eight event shapes into one wide object and lose the
 * discrimination that makes them safe to switch on.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never
export type UnsequencedEvent = DistributiveOmit<SessionEvent, 'seq'>
